import * as React from 'react';
import * as styles from './side-nav.module.scss';
import { useSideNavigation } from '../../../queries/side-navigation';
import Anchor from '../../anchor/anchor';
import ThinArrow from '../../icons/thin-arrow';
import ChevronRight from '../../icons/chevron-right';
import ChevronDown from '../../icons/chevron-down';
import { CurrentNavSegmentsContext } from '../../../hooks/current-nav-segments';
import classNames from '../../../utilities/class-names';
import { useLocation } from '../../../hooks/location';

let expandingInProgress = false;

const SfSideNav = () => {
    const navRoot = useSideNavigation();
    const location = useLocation();
    const [manuallyExpandedSegment, setManuallyExpandedSegment] = React.useState(null);

    const currentNavSegments = React.useContext(CurrentNavSegmentsContext);

    const currentRoot = React.useMemo(() => {
        if (manuallyExpandedSegment) {
            return manuallyExpandedSegment;
        }

        if (currentNavSegments.length > 1) {
            return currentNavSegments[currentNavSegments.length - 1];
        }

        return navRoot;
    }, [currentNavSegments, manuallyExpandedSegment, navRoot]);

    const isSubroot = currentRoot !== navRoot;

    const sectionSlug = React.useMemo(() => {
        const url = currentRoot?.relativeUrl || currentRoot?.childrenSegments?.[0]?.relativeUrl || '';
        return url.split('/').filter(Boolean)[0] || '';
    }, [currentRoot]);

    React.useEffect(() => {
        expandingInProgress = false;
        setManuallyExpandedSegment(null);
    }, [location]);

    return <>
        <div className={classNames(styles.container, isSubroot && styles.subrootContainer)} data-section={sectionSlug}>
            {isSubroot && <>
                <div>
                    <button onClick={() => setManuallyExpandedSegment(navRoot)} className={styles.btnAllTopics} aria-label='Expand'>
                        <div>
                            <ThinArrow className={classNames(styles.backIcon, styles.backIconDesktop)} />
                            <ThinArrow className={classNames(styles.backIcon, styles.backIconMobile)} viewBox="3.19922 4.19922 13.72298 11.72288" preserveAspectRatio="none" />
                        </div>
                        <div className={styles.btnAllTopicsText}>All topics</div>
                        <div className={styles.btnSectionTitle}>{currentRoot.title}</div>
                    </button>
                </div>
            </>}
            <div className={styles.title}>
                {!isSubroot ? 'Topics' : currentRoot.title}
            </div>
            <div className={styles.sectionsWrap}>
                {
                    currentRoot.childrenSegments.filter(s => !s.hidden).map(navSegment => <React.Fragment key={navSegment.id}>
                        {isSubroot ? <>
                            <SideNavSection navSegment={navSegment} />
                        </> : <>
                            <button className={styles.navTopic} onClick={() => setManuallyExpandedSegment(navSegment)}>
                                <div>{navSegment.title}</div>
                                <div><ChevronRight className={styles.topicIcon} /></div>
                            </button>
                        </>}
                    </React.Fragment>)
                }
            </div>
        </div>
    </>
}

const SideNavSection = ({ navSegment }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [active, setActive] = React.useState(false);
    const [animate, setAnimate] = React.useState(false);
    const childrenContainerRef = React.useRef(null);

    const currentNavSegments = React.useContext(CurrentNavSegmentsContext);

    React.useEffect(() => {
        if (currentNavSegments.includes(navSegment) && !expandingInProgress) {
            setExpanded(true);
        }

        const isActive = currentNavSegments.find(s => !s.indexPage) === navSegment;
        setActive(isActive);

    }, [currentNavSegments, navSegment]);

    const { children, relativeUrl } = React.useMemo(() => {
        let visibleChildren = [];

        if (!navSegment.hideChildren && navSegment.childrenSegments) {
            visibleChildren = navSegment.childrenSegments.filter(child => !child.hidden);
        }

        let relativeUrl = navSegment.relativeUrl;

        if (!relativeUrl) {
            relativeUrl = visibleChildren.find(c => c.indexPage)?.relativeUrl;
            visibleChildren = visibleChildren.filter(c => !c.indexPage);
        }

        return { children: visibleChildren, relativeUrl };
    }, [navSegment]);

    const Icon = expanded ? ChevronDown : ChevronRight;

    const toggleChildren = () => {
        expandingInProgress = true;
        const element = childrenContainerRef.current;

        element.style.height = `${element.firstChild.offsetHeight}px`;

        setAnimate(true);
        setExpanded(!expanded);
    };

    React.useLayoutEffect(() => {
        if (!animate) {
            return;
        }

        const element = childrenContainerRef.current;
        element.style.height = `${element.firstChild.offsetHeight}px`;
    }, [expanded, animate]);

    const onTransitionEnd = () => {
        childrenContainerRef.current.style.height = null
        expandingInProgress = false;
    }

    return <>
        <div className={styles.navSection}>
            <div className={styles.navNode}>
                {children.length > 0 && <>
                    <button onClick={toggleChildren} className={styles.expander} aria-label='Expand'>
                        <Icon className={styles.expanderIcon} />
                    </button>
                </>}
                <Anchor href={relativeUrl} className={classNames(styles.navLink, active && styles.active)}>{navSegment.title}</Anchor>
            </div>
            {
                children.length > 0 && <>
                    <div ref={childrenContainerRef} className={styles.childContainer} onTransitionEnd={onTransitionEnd}>
                        <div>
                            {expanded && children.map(childSegment => <React.Fragment key={childSegment.id}>
                                <SideNavSection navSegment={childSegment} />
                            </React.Fragment>)}
                        </div>
                    </div>
                </>
            }
        </div>
    </>
}

export default SfSideNav;